/**
 * Material role vocabulary.
 *
 * A *style* is a named bundle of property values. Its material half addresses
 * seven conventional roles, and each role is a family of properties by suffix:
 *
 *   structureSrc   structureColor   structureRoughness   structureMetallic
 *
 * These are **conventional names, not schema** — a style sets whatever pairs it
 * wants, and partial styles are normal. Nothing here validates a style; it only
 * supplies the shared vocabulary so the editor, the adoption planner and the
 * style authoring surface all spell the same property the same way.
 *
 * The role set and its order are the prototype's. The *spelling* is ours:
 * these are Canopy property names, so they follow the engine's camelCase
 * convention rather than the prototype's `Accent_01`. Adoption reads the
 * prototype's spelling through the alias table below, so nothing is lost.
 */

/**
 * The seven material roles, in canonical order. Index is the role's ordinal.
 *
 * camelCase, matching every other property name in the engine — Canopy
 * declarations read `bodyColor` / `primaryColor` / `fontSize`, and exactly one
 * of 171 behavior-data fields carries an underscore (`texture_transform`,
 * borrowed from glTF). The prototype spells these `Accent_01`; that spelling is
 * an adoption *input*, folded away by {@link normalizeRoleKey}, not a storage
 * format we need to mirror.
 */
export const MATERIAL_ROLES = [
    'structure',
    'horizontalSurface',
    'accent1',
    'verticalSurface',
    'accent2',
    'lightSubtle',
    'darkSubtle',
] as const;

export type MaterialRole = typeof MATERIAL_ROLES[number];

/** Property suffixes a role can carry. */
export const ROLE_SUFFIXES = ['src', 'color', 'roughness', 'metallic'] as const;

export type RoleSuffix = typeof ROLE_SUFFIXES[number];

/**
 * The property name a role/suffix pair addresses — `accent1` + `src` →
 * `accent1Src`.
 *
 * camelCase, for the reason given on {@link MATERIAL_ROLES}: this is an
 * ordinary Canopy property name and lives in the same namespace as every other
 * one.
 */
export function rolePropertyName(role: MaterialRole, suffix: RoleSuffix): string {
    return `${role}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
}

/**
 * Alias table for adoption. Recognized when reading an asset, **never stored**.
 *
 * Covers the V2 canonical names and the V1 numbered scheme — exactly the names
 * the prototype's own parser accepted. V1 had six slots, so V1 content never
 * fills `darkSubtle`. Keys are normalized (see {@link normalizeRoleKey}), so
 * one entry covers every separator and casing variant of the same name.
 *
 * Deliberately absent: the V1 slots' semantic labels (`primary`, `secondary`,
 * `accent`, `backdrop`, `support`, `special`). They were documentation, not a
 * naming convention the prototype recognized — and as bare generic words they
 * appear in arbitrary content and collide with the **color-role** namespace,
 * where a mesh material named `accent` means "paint with the accent color",
 * not "receive the accent1 material". Matching them here would silently
 * hijack that content into the wrong axis.
 */
const ROLE_ALIASES: Readonly<Record<string, MaterialRole>> = {
    // V2 canonical — the names the prototype writes into GLB materials.
    structure: 'structure',
    horizontalsurface: 'horizontalSurface',
    accent01: 'accent1',
    verticalsurface: 'verticalSurface',
    accent02: 'accent2',
    lightsubtle: 'lightSubtle',
    darksubtle: 'darkSubtle',
    // V1 numbered, by slot position.
    material01: 'structure',
    base1: 'structure',
    material02: 'horizontalSurface',
    base2: 'horizontalSurface',
    material03: 'accent1',
    base3: 'accent1',
    material04: 'verticalSurface',
    base4: 'verticalSurface',
    material05: 'accent2',
    base5: 'accent2',
    material06: 'lightSubtle',
    base6: 'lightSubtle',
};

/**
 * Fold a GLB material name to its alias key: case-insensitive, and blind to the
 * separators authoring tools disagree about. `Accent_01`, `accent-01`,
 * `Accent 01` and `ACCENT01` are one name.
 *
 * Trailing `.001`-style suffixes are stripped too — Blender appends them on
 * duplicate material names, and a duplicated `Structure` is still structure.
 */
export function normalizeRoleKey(materialName: string): string {
    return materialName
        .trim()
        .replace(/\.\d+$/, '')
        .replace(/[\s_-]+/g, '')
        .toLowerCase();
}

/**
 * The role a GLB material name declares, or `null` if it declares none.
 *
 * `null` is the common case for production content — surveyed real assets name
 * their materials `mat20`, `Grey`, `Coffee_Metal_01`. Callers fall back to a
 * positional strategy for those; see the adoption planner.
 */
export function resolveMaterialRole(materialName: string | undefined): MaterialRole | null {
    if (!materialName) return null;
    const key = normalizeRoleKey(materialName);
    return Object.hasOwn(ROLE_ALIASES, key) ? ROLE_ALIASES[key] : null;
}

/**
 * The role at a given ordinal, cycling so a palette always fills an object with
 * more material groups than there are roles.
 */
export function roleForOrdinal(ordinal: number): MaterialRole {
    const count = MATERIAL_ROLES.length;
    return MATERIAL_ROLES[((ordinal % count) + count) % count];
}

const PROPERTY_NAME_TO_ROLE: ReadonlyMap<string, MaterialRole> = new Map(
    MATERIAL_ROLES.flatMap(role =>
        ROLE_SUFFIXES.map(suffix => [rolePropertyName(role, suffix), role] as const),
    ),
);

/**
 * The role a role-family property name belongs to (`accent1Src` → `accent1`),
 * or `null` for names outside the vocabulary. The inverse of
 * {@link rolePropertyName}; the adoption planner uses it to read which roles an
 * object's existing bindings already occupy.
 */
export function roleForPropertyName(propertyName: string): MaterialRole | null {
    return PROPERTY_NAME_TO_ROLE.get(propertyName) ?? null;
}
